import { z } from "zod";
import { ProjectFile, ProjectFileType } from "@/lib/api/schemas/projectFile";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

/**
 * An API project's knowledge files. Writes opt into local error handling: the dialogs show
 * the backend's own message inline (a duplicate name is a 409 whose `message` says so), so
 * a global toast on top would say it twice.
 */
class ProjectFilesEndpoint {
  public static readonly basePath = "project-files";
  public static readonly schemas = {
    list: {
      response: z.object({ count: z.number(), items: ProjectFile.array() }),
    },
    read: { response: ProjectFile },
    create: {
      body: z.object({
        name: z.string().min(1),
        type: ProjectFileType,
        description: z.string(),
        file: z.instanceof(File),
      }),
    },
    update: {
      body: z.object({
        name: z.string().min(1).optional(),
        description: z.string().optional(),
        type: ProjectFileType.optional(),
        is_active: z.boolean().optional(),
      }),
    },
  };

  constructor(private api: Auth) {}

  public list(query: {
    project_id: number;
    /** The backend's default page is 100 — well past what a project keeps, so one page is all of them. */
    limit?: number;
  }): ActionReturnType<z.infer<typeof ProjectFilesEndpoint.schemas.list.response>> {
    return request({
      log: ["ProjectFiles", "list"],
      method: "get",
      path: [ProjectFilesEndpoint.basePath],
      query,
      response: ProjectFilesEndpoint.schemas.list.response,
    });
  }

  /** The file's own bytes, as text — every kind the panel accepts is plain text. */
  public download(id: number): ActionReturnType<string> {
    return request({
      log: ["ProjectFiles", "download"],
      method: "get",
      path: [ProjectFilesEndpoint.basePath, id, "download"],
      response: z.string(),
      responseAs: "text",
    });
  }

  public create(
    projectId: number,
    input: z.input<typeof ProjectFilesEndpoint.schemas.create.body>,
  ): ActionReturnType<z.infer<typeof ProjectFilesEndpoint.schemas.read.response>> {
    const valid = validateInput(ProjectFilesEndpoint.schemas.create.body, input);
    if (!valid.ok) return Promise.resolve(valid);

    const formData = new FormData();
    formData.append("name", valid.data.name);
    formData.append("type", valid.data.type);
    formData.append("description", valid.data.description);
    formData.append("file", valid.data.file);

    return request({
      log: ["ProjectFiles", "create"],
      method: "post",
      path: [ProjectFilesEndpoint.basePath, projectId, "add-file"],
      formData,
      localErrorHandling: true,
      response: ProjectFilesEndpoint.schemas.read.response,
    });
  }

  /** Partial: an omitted field stays as it is. */
  public update(
    id: number,
    body: z.input<typeof ProjectFilesEndpoint.schemas.update.body>,
  ): ActionReturnType<z.infer<typeof ProjectFilesEndpoint.schemas.read.response>> {
    const valid = validateInput(ProjectFilesEndpoint.schemas.update.body, body);
    if (!valid.ok) return Promise.resolve(valid);

    return request({
      log: ["ProjectFiles", "update"],
      method: "patch",
      path: [ProjectFilesEndpoint.basePath, id],
      json: valid.data,
      localErrorHandling: true,
      response: ProjectFilesEndpoint.schemas.read.response,
    });
  }

  /** New bytes under the same record: name, description, type and state are kept. */
  public replace(id: number, file: File): ActionReturnType<z.infer<typeof ProjectFilesEndpoint.schemas.read.response>> {
    const formData = new FormData();
    formData.append("file", file);

    return request({
      log: ["ProjectFiles", "replace"],
      method: "post",
      path: [ProjectFilesEndpoint.basePath, id, "replace"],
      formData,
      localErrorHandling: true,
      response: ProjectFilesEndpoint.schemas.read.response,
    });
  }

  /** Soft on the backend: gone from every list, and the name is free again. */
  public delete(id: number): ActionReturnType<undefined> {
    return request({
      log: ["ProjectFiles", "delete"],
      method: "delete",
      path: [ProjectFilesEndpoint.basePath, id],
      localErrorHandling: true,
    });
  }
}

export { ProjectFilesEndpoint };
